import { useEffect, useRef, useState } from 'react';

import { DateTime } from 'luxon';

import { useQuery } from '@apollo/client/react';

import { useLatestRef } from '@clutter/hooks';

import {
  AvailabilitiesDocument,
  AvailabilitiesQuery,
  AvailabilitiesQueryVariables,
  LongDistancePickupAvailabilitiesDocument,
  MovingQuoteFragment,
  OrderServiceTypeEnum,
  OrderSubtypeEnum,
  OrderTypeEnum,
} from '@graphql/platform';

import { normalizeDate } from '@shared/calendar/calendar_utils';

import { clampAvailabilities } from '@utils/availability';

const OFFSET = { days: 1 };
const DEFAULT_INCREMENT = { weeks: 2 };
const INITIAL_OFFSET = { weeks: 4 };
const FETCH_MORE_MIN_INTERVAL_MS = 400;
const RATE_LIMIT_BACKOFF_MS = 5000;

const NO_AVAILABILITY: AvailabilitiesQuery['availabilities'] = [];

const isAvailabilityRateLimitError = (error: unknown): boolean => {
  if (typeof error === 'object' && error !== null) {
    const record = error as Record<string, unknown>;
    if (record.statusCode === 429) return true;
    const networkError = record.networkError;
    if (
      typeof networkError === 'object' &&
      networkError !== null &&
      (networkError as Record<string, unknown>).statusCode === 429
    ) {
      return true;
    }
  }

  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'object' && error && 'message' in error
        ? String((error as { message?: unknown }).message)
        : String(error ?? '');
  return /too many requests/i.test(message);
};

type AvailabilityStreetAddress = {
  street?: string;
  city?: string;
  state?: string;
  latitude?: number | null;
  longitude?: number | null;
};

type BaseAvailabilityParams = {
  zip: string;
  /** When set, fetch windows for this street address instead of ZIP-only. */
  address?: AvailabilityStreetAddress;
  orderType: OrderTypeEnum;
  orderSubtype: OrderSubtypeEnum;
  startDate?: DateTime;
  tillDate?: DateTime;
  skip?: boolean;
  /** Clamp availabilities to the provided dates and read from the cache only.  */
  clamp?: boolean;
  /** Challenger funnel only: throttle paging and prefer cache to reduce rate limits. */
  conserveRequests?: boolean;
};

type StorageAvailabilityParams = {
  expectedPlanID: string | undefined; // Included for better type safety
  serviceType: OrderServiceTypeEnum | undefined; // Included for better type safety
};

type MovingAvailabilityParams = {
  movers?: number;
  movingQuote?: MovingQuoteFragment;
  expectedPlanID?: string;
};

const useAvailabilities = ({
  zip,
  address,
  orderType,
  orderSubtype,
  expectedPlanID,
  startDate,
  tillDate,
  skip,
  movers,
  serviceType,
  clamp,
  movingQuote,
  conserveRequests = false,
}: BaseAvailabilityParams &
  Partial<StorageAvailabilityParams & MovingAvailabilityParams>) => {
  const from =
    startDate ||
    DateTime.local().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  const till = tillDate || from.plus(INITIAL_OFFSET);
  const [cursor, setCursor] = useState<DateTime>(till);
  const tillRef = useLatestRef(till);
  const fetchMoreInFlightRef = useRef(false);
  const lastFetchMoreAtRef = useRef(0);
  const rateLimitedUntilRef = useRef(0);
  const pagingGenerationRef = useRef(0);
  const street = address?.street?.trim() ?? '';
  const city = address?.city?.trim() ?? '';
  const state = address?.state?.trim() ?? '';
  const latitude = address?.latitude ?? undefined;
  const longitude = address?.longitude ?? undefined;
  const scopeKey = [
    zip,
    street,
    city,
    state,
    latitude ?? '',
    longitude ?? '',
    expectedPlanID,
    serviceType,
    orderType,
    orderSubtype,
    movingQuote?.signedID,
  ].join('|');

  const buildInput = (
    inputFrom: DateTime = from,
    inputTill: DateTime = till,
  ): AvailabilitiesQueryVariables['input'] => {
    return {
      from: inputFrom.toISO() ?? '',
      till: inputTill.toISO() ?? '',
      order: {
        address: {
          zip,
          street,
          city,
          state,
          latitude,
          longitude,
        },
        type: orderType,
        subtype: orderSubtype,
        expectedPlanID,
        movers,
        serviceType,
      },
      movingQuoteSignedID: movingQuote?.signedID,
    };
  };

  const longDistance = !!movingQuote?.longDistance;

  useEffect(() => {
    // If any input parameter changes that could impact availability, we reset
    // the paged availability to make sure it can be re-fetched. Alternately, we
    // could imperatively re-fetch from till -> cursor but this isn't a common
    // occurance.
    setCursor(tillRef.current);
    pagingGenerationRef.current += 1;
    if (conserveRequests) {
      fetchMoreInFlightRef.current = false;
      lastFetchMoreAtRef.current = 0;
      rateLimitedUntilRef.current = 0;
    }
  }, [
    zip,
    street,
    city,
    state,
    latitude,
    longitude,
    orderType,
    orderSubtype,
    expectedPlanID,
    movers,
    serviceType,
    tillRef,
    longDistance,
    conserveRequests,
  ]);

  const availabilityFetchPolicy = clamp
    ? 'cache-only'
    : conserveRequests
      ? 'cache-first'
      : undefined;

  const standardQueryResult = useQuery(AvailabilitiesDocument, {
    variables: { input: buildInput() },
    fetchPolicy: availabilityFetchPolicy,
    skip: skip || longDistance,
  });

  const longDistanceQueryResult = useQuery(
    LongDistancePickupAvailabilitiesDocument,
    {
      variables: {
        from: from.toISO(),
        till: till.toISO(),
        movingQuoteSignedID: movingQuote?.signedID || '',
        zip,
      },
      fetchPolicy: availabilityFetchPolicy,
      skip: skip || !longDistance,
    },
  );

  const { data, loading, error } = longDistance
    ? longDistanceQueryResult
    : standardQueryResult;

  useEffect(() => {
    if (
      !conserveRequests ||
      loading ||
      !error ||
      !isAvailabilityRateLimitError(error)
    ) {
      return;
    }
    rateLimitedUntilRef.current = Date.now() + RATE_LIMIT_BACKOFF_MS;
  }, [conserveRequests, error, loading]);

  const queryAvailabilities = data?.availabilities;
  const [scopedResult, setScopedResult] = useState<{
    key: string;
    items: AvailabilitiesQuery['availabilities'];
  } | null>(null);

  useEffect(() => {
    if (skip) {
      setScopedResult(null);
      return;
    }
    if (loading && queryAvailabilities == null && !error) return;
    setScopedResult({
      key: scopeKey,
      items: queryAvailabilities ?? NO_AVAILABILITY,
    });
  }, [error, loading, queryAvailabilities, scopeKey, skip]);

  const availabilities =
    scopedResult?.key === scopeKey ? scopedResult.items : NO_AVAILABILITY;
  const clampedAvailabilities =
    clamp && startDate && tillDate
      ? clampAvailabilities(availabilities, { from: startDate, till: tillDate })
      : availabilities;

  const normalizedDate = normalizeDate(from);
  // Use normalized date if in same month, otherwise fallback to first day of original month
  // to prevent crossing month boundaries when going back a week
  const resolvedDate =
    normalizedDate.month === from.month
      ? normalizedDate
      : from.plus({ days: 1 - from.day });

  const fetchMore = () => {
    if (skip) return;

    const current = cursor;
    const nextFrom = current.plus(OFFSET);
    const nextTill = current.plus(DEFAULT_INCREMENT);

    if (!conserveRequests) {
      setCursor(current.plus(DEFAULT_INCREMENT));
      if (longDistance) {
        longDistanceQueryResult.fetchMore({
          variables: {
            zip,
            from: nextFrom.toISO(),
            till: nextTill.toISO(),
            movingQuoteSignedID: movingQuote?.signedID || '',
          },
        });
      } else {
        standardQueryResult.fetchMore({
          variables: {
            input: buildInput(nextFrom, nextTill),
          },
        });
      }
      return;
    }

    if (Date.now() < rateLimitedUntilRef.current) return;
    if (fetchMoreInFlightRef.current) return;
    if (Date.now() - lastFetchMoreAtRef.current < FETCH_MORE_MIN_INTERVAL_MS) {
      return;
    }

    const requestGeneration = pagingGenerationRef.current;
    const nextCursor = current.plus(DEFAULT_INCREMENT);
    lastFetchMoreAtRef.current = Date.now();
    fetchMoreInFlightRef.current = true;

    const isActiveRequest = () =>
      requestGeneration === pagingGenerationRef.current;

    const completeFetchMore = () => {
      if (isActiveRequest()) {
        fetchMoreInFlightRef.current = false;
      }
    };

    const handleFetchMoreSuccess = () => {
      if (isActiveRequest()) {
        setCursor(nextCursor);
      }
    };

    const handleFetchMoreFailure = (fetchError: unknown) => {
      if (isActiveRequest()) {
        setCursor(current);
        if (isAvailabilityRateLimitError(fetchError)) {
          rateLimitedUntilRef.current = Date.now() + RATE_LIMIT_BACKOFF_MS;
        }
      }
    };

    if (longDistance) {
      void longDistanceQueryResult
        .fetchMore({
          variables: {
            zip,
            from: nextFrom.toISO(),
            till: nextTill.toISO(),
            movingQuoteSignedID: movingQuote?.signedID || '',
          },
        })
        .then(handleFetchMoreSuccess)
        .catch(handleFetchMoreFailure)
        .finally(completeFetchMore);
    } else {
      void standardQueryResult
        .fetchMore({
          variables: {
            input: buildInput(nextFrom, nextTill),
          },
        })
        .then(handleFetchMoreSuccess)
        .catch(handleFetchMoreFailure)
        .finally(completeFetchMore);
    }
  };

  return {
    from: resolvedDate,
    till: cursor,
    availabilities: clampedAvailabilities,
    fetchMore,
    loading,
    fetched: scopedResult?.key === scopeKey,
    error,
  };
};

export const useStorageAvailabilities = (
  params: BaseAvailabilityParams & StorageAvailabilityParams,
) => useAvailabilities(params);
